import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import AuthenticatedLayout from '../Layouts/AuthenticatedLayout';
import { useAuth } from '../context/AuthContext';
import axios from '../axios';
import { Button } from '../shadcn/ui/button';
import { Checkbox } from '../shadcn/ui/checkbox';
import { toast } from '@/shadcn/ui/use-toast';

const CHILD_KEYS = ['children', 'children_recursive', 'childrenRecursive', 'children_recursive_with_products'];

const getChildNodes = (node) => {
  if (!node) return [];
  for (const key of CHILD_KEYS) {
    if (Array.isArray(node[key])) return node[key];
  }
  return [];
};

const normalizeNode = (node) => {
  if (!node) return null;
  const children = getChildNodes(node)
    .map((child) => normalizeNode(child))
    .filter(Boolean);

  return {
    ...node,
    id: String(node.id),
    name: node.name || node.slug || `Κατηγορία #${node.id}`,
    children,
    products: Array.isArray(node.products) ? node.products : [],
  };
};

const parseProductsResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.products)) return payload.products;
  if (Array.isArray(payload.items)) return payload.items;
  return [];
};

const getProductCategoryId = (product) =>
  product?.category_id ?? product?.category?.id ?? product?.categoryId ?? null;

const getOwnProductsForCategory = (categoryId, productsByCategory) =>
  (productsByCategory[categoryId] || []).filter(
    (product) => String(getProductCategoryId(product)) === String(categoryId)
  );

const CategoryNode = ({
  node,
  depth = 0,
  expandedIds,
  onToggleExpand,
  productsByCategory,
  loadingProducts,
  onProductToggle,
  selectedProductIds,
}) => {
  const isExpanded = expandedIds.has(node.id);
  const products = getOwnProductsForCategory(node.id, productsByCategory);
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const isLoadingProducts = loadingProducts.has(node.id);

  const shouldShowToggle = hasChildren || isExpanded || products.length || isLoadingProducts;

  return (
    <div className="space-y-2">
      <div
        className="flex items-center gap-2 rounded border bg-white px-3 py-2 shadow-sm"
        style={{ marginLeft: depth * 12 }}
      >
        <button
          type="button"
          className="flex items-center gap-2 text-left text-gray-800"
          onClick={() => shouldShowToggle && onToggleExpand(node)}
          disabled={!shouldShowToggle}
        >
          {shouldShowToggle ? (
            isExpanded ? (
              <ChevronDownIcon className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRightIcon className="h-4 w-4 text-gray-500" />
            )
          ) : (
            <span className="h-4 w-4" />
          )}
          <span className="font-medium">{node.name}</span>
        </button>
      </div>

      {isExpanded && (
        <div className="space-y-3" style={{ marginLeft: depth * 12 + 24 }}>
          {isLoadingProducts && (
            <p className="text-xs text-gray-500">Φόρτωση προϊόντων...</p>
          )}

          {products.length > 0 && (
            <div className="space-y-2 rounded border bg-white p-3">
              {products.map((product) => {
                const productId = String(product.product_id || product.id);
                return (
                  <label
                    key={productId}
                    className="flex items-center gap-3 rounded px-2 py-1 hover:bg-gray-50"
                  >
                    <Checkbox
                      checked={selectedProductIds.has(productId)}
                      onCheckedChange={(checked) =>
                        onProductToggle(product, Boolean(checked))
                      }
                    />
                    <span className="text-sm text-gray-800">
                      {product.name || product.title || product.slug || `Product #${productId}`}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {hasChildren &&
            node.children.map((child) => (
              <CategoryNode
                key={child.id}
                node={child}
                depth={depth + 1}
                expandedIds={expandedIds}
                onToggleExpand={onToggleExpand}
                productsByCategory={productsByCategory}
                loadingProducts={loadingProducts}
                onProductToggle={onProductToggle}
                selectedProductIds={selectedProductIds}
              />
            ))}
        </div>
      )}
    </div>
  );
};

export default function PrivateBlendPage() {
  const { user } = useAuth();
  const [categoryTree, setCategoryTree] = useState([]);
  const [isTreeLoading, setIsTreeLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [productsByCategory, setProductsByCategory] = useState({});
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingSelection, setIsLoadingSelection] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(new Set());
  const [categoryProductMeta, setCategoryProductMeta] = useState({});

  const normalizedTree = useMemo(
    () => (categoryTree || []).map((node) => normalizeNode(node)).filter(Boolean),
    [categoryTree]
  );

  const fetchPrivateBlendTree = useCallback(async () => {
    setIsTreeLoading(true);
    try {
      const response = await axios.get('/api/private-blend/categories');
      const categories = Array.isArray(response.data?.categories)
        ? response.data.categories
        : Array.isArray(response.data)
          ? response.data
          : [];

      const normalized = categories.map((node) => normalizeNode(node)).filter(Boolean);
      const productsMap = {};
      const metaMap = {};
      const productIdsFromTree = [];

      const traverse = (nodes) => {
        nodes.forEach((node) => {
          if (!node) return;
          const ownProducts = Array.isArray(node.products) ? node.products : [];
          productsMap[node.id] = ownProducts;
          metaMap[node.id] = { loaded: true, hasProducts: ownProducts.length > 0 };
          ownProducts.forEach((product) => {
            const pid = String(product.product_id || product.id || '');
            if (pid) {
              productIdsFromTree.push(pid);
            }
          });
          if (Array.isArray(node.children) && node.children.length) {
            traverse(node.children);
          }
        });
      };

      traverse(normalized);
      setCategoryTree(normalized);
      setProductsByCategory(productsMap);
      setCategoryProductMeta(metaMap);
      setSelectedProductIds((prev) => {
        if (prev && prev.size) {
          const next = new Set(prev);
          productIdsFromTree.forEach((id) => next.add(id));
          return next;
        }
        return new Set(productIdsFromTree);
      });
    } catch (error) {
      console.error('Failed to load private blend categories with products', error);
      toast({
        title: 'Σφάλμα',
        description: 'Δεν ήταν δυνατή η φόρτωση των κατηγοριών Private Blend.',
        variant: 'error',
      });
    } finally {
      setIsTreeLoading(false);
      setLoadingProducts(new Set());
    }
  }, []);

  const pruneTreeForProducts = useCallback(
    (nodes = []) => {
      const pruned = [];
      nodes.forEach((node) => {
        if (!node) return;
        const childPruned = pruneTreeForProducts(node.children || []);
        const ownProducts = getOwnProductsForCategory(node.id, productsByCategory);
        const hasProducts = ownProducts.length > 0;
        const meta = categoryProductMeta[node.id];
        const shouldKeep =
          hasProducts ||
          childPruned.length > 0 ||
          !meta?.loaded; // keep until confirmed empty

        if (shouldKeep) {
          pruned.push({
            ...node,
            children: childPruned,
            hasProducts,
          });
        }
      });
      return pruned;
    },
    [productsByCategory, categoryProductMeta]
  );

  const prunedTree = useMemo(
    () => pruneTreeForProducts(normalizedTree),
    [normalizedTree, pruneTreeForProducts]
  );

  const allLoadedProductIds = useMemo(() => {
    const ids = [];
    Object.keys(productsByCategory).forEach((categoryId) => {
      getOwnProductsForCategory(categoryId, productsByCategory).forEach((product) => {
        const pid = String(product.product_id || product.id);
        if (pid) ids.push(pid);
      });
    });
    return ids;
  }, [productsByCategory]);

  const handleSelectAllLoaded = () => {
    setSelectedProductIds(new Set(allLoadedProductIds));
  };

  const handleClearSelection = () => {
    setSelectedProductIds(new Set());
  };

  const handleToggleExpand = useCallback(
    (category) => {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (next.has(category.id)) {
          next.delete(category.id);
        } else {
          next.add(category.id);
        }
        return next;
      });
    },
    []
  );

  const handleProductToggle = useCallback((product, isChecked) => {
    const productId = String(product.product_id || product.id);
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (isChecked) {
        next.add(productId);
      } else {
        next.delete(productId);
      }
      return next;
    });
  }, []);

  const loadExistingSelection = useCallback(async () => {
    setIsLoadingSelection(true);
    try {
      const response = await axios.get('/api/private-blend/products');
      const ids =
        response?.data?.product_ids ||
        response?.data?.products?.map((p) => p.product_id || p.id) ||
        [];
      setSelectedProductIds(new Set((ids || []).map((id) => String(id))));
    } catch (error) {
      console.error('Failed to load private blend selection', error);
    } finally {
      setIsLoadingSelection(false);
    }
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.post('/api/private-blend/products', {
        product_ids: Array.from(selectedProductIds),
      });
      toast({
        title: 'Αποθήκευση',
        description: 'Οι επιλογές προϊόντων αποθηκεύτηκαν.',
        variant: 'success',
      });
    } catch (error) {
      const message =
        error.response?.data?.message ||
        'Αποτυχία αποθήκευσης επιλογών.';
      toast({ title: 'Σφάλμα', description: message, variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    document.title = 'Private Blend - Coffee Roast City';
  }, []);

  useEffect(() => {
    loadExistingSelection();
    fetchPrivateBlendTree();
  }, [loadExistingSelection, fetchPrivateBlendTree]);

  return (
    <AuthenticatedLayout user={user}>
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Private Blend</h1>
          <p className="text-sm text-gray-600">
            Επιλέξτε προϊόντα από τις κατηγορίες για να τα χρησιμοποιήσετε στο "Φτιάξε το blend σου".
          </p>
        </div>

        <div className="rounded-md border bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <p className="text-sm text-gray-700">
                Επιλογές προϊόντων
              </p>
              <p className="text-xs text-gray-500">
                Επιλεγμένα: {selectedProductIds.size} {allLoadedProductIds.length ? `(φορτωμένα: ${allLoadedProductIds.length})` : ''}
              </p>
              {(isTreeLoading || isLoadingSelection) && (
                <p className="text-xs text-gray-500">Φόρτωση δεδομένων...</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={handleSelectAllLoaded} disabled={!allLoadedProductIds.length}>
                Επιλογή όλων (φορτωμένων)
              </Button>
              <Button variant="secondary" onClick={handleClearSelection} disabled={!selectedProductIds.size}>
                Εκκαθάριση
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Αποθήκευση...' : 'Αποθήκευση επιλογών'}
              </Button>
            </div>
          </div>

          <div className="my-4 h-px bg-gray-200" />

          <div className="space-y-3">
            {prunedTree.length === 0 && (
              <p className="text-sm text-gray-500">Δεν βρέθηκαν κατηγορίες.</p>
            )}
            {prunedTree.map((node) => (
              <CategoryNode
                key={node.id}
                node={node}
                expandedIds={expandedIds}
                onToggleExpand={handleToggleExpand}
                productsByCategory={productsByCategory}
                loadingProducts={loadingProducts}
                onProductToggle={handleProductToggle}
                selectedProductIds={selectedProductIds}
              />
            ))}
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
}
